import React from 'react';
import { useSelector } from 'react-redux';
import { Tag, PageHeader, BackTop, Table } from 'antd';
import { useNavigate } from 'react-router-dom';
import type { ColumnsType } from 'antd/es/table';
import { useGetAuctionReadyVehiclesQuery } from '../../../app/api/vehicleApiSlice';
import { formatDateTime } from '../../../utils/dateTimeUtil';
import VehicleStatus from '../../../core/enums/VehicleStatus';
import Vehicle from '../../../core/models/Vehicle';
import VehicleStatusColorCode from '../../../core/enums/VehicleStatusColorCode';
import tableColumnTextFilterConfig from '../../../utils/searchUtil';
import {
  getVehicleColors,
  getVehicleMakes,
  getVehicleModels,
} from '../../../app/store/commonSlice';

const NextAuction: React.FC = () => {
  const navigate = useNavigate();

  const { data: vehicles, isLoading } = useGetAuctionReadyVehiclesQuery();

  const vehicleMakes = useSelector(getVehicleMakes);
  const vehicleModels = useSelector(getVehicleModels);
  const vehicleColors = useSelector(getVehicleColors);

  const statusFilter = Object.values(VehicleStatus)
    .filter((value) => isNaN(Number(value)))
    .map((status) => ({
      text: status.toString(),
      value: status.toString(),
    }));

  const columns: ColumnsType<Vehicle> = [
    {
      title: 'VID',
      dataIndex: 'code',
      key: 'code',
      sorter: (a, b) => a.code.localeCompare(b.code),
      render: (text: string) => text,
      ...tableColumnTextFilterConfig<Vehicle>(),
      onFilter: (value, record) => {
        return record.code.toString().toLowerCase().includes(value.toString().toLowerCase());
      },
    },
    {
      title: 'Registration No.',
      dataIndex: 'plateNumber',
      key: 'plateNumber',
      sorter: (a, b) => a.plateNumber.localeCompare(b.plateNumber),
      render: (text: string) => text,
      ...tableColumnTextFilterConfig<Vehicle>(),
      onFilter: (value, record) => {
        return record.plateNumber.toString().toLowerCase().includes(value.toString().toLowerCase());
      },
    },
    {
      title: 'Make',
      dataIndex: 'make',
      key: 'make',
      render: (value: number) => vehicleMakes?.find((make) => make.id === value)?.name,
      sorter: (a, b) => a.make - b.make,
      filters: vehicleMakes?.map((make) => ({ text: make.name, value: make.id })),
      filterSearch: true,
      onFilter: (value, record) => {
        return record.make === value;
      },
      responsive: ['md'],
    },
    {
      title: 'Model',
      dataIndex: 'model',
      key: 'model',
      render: (value: number) => vehicleModels?.find((model) => model.id === value)?.name,
      sorter: (a, b) => a.model - b.model,
      filters: vehicleModels?.map((model) => ({ text: model.name, value: model.id })),
      filterSearch: true,
      onFilter: (value, record) => {
        return record.model === value;
      },
      responsive: ['md'],
    },
    {
      title: 'YoM',
      dataIndex: 'yom',
      key: 'yom',
      sorter: (a, b) => a.yom - b.yom,
      render: (text: string) => text,
      ...tableColumnTextFilterConfig<Vehicle>(),
      onFilter: (value, record) => {
        return record.yom.toString().toLowerCase().includes(value.toString().toLowerCase());
      },
      responsive: ['md'],
    },
    {
      title: 'Color',
      dataIndex: 'color',
      key: 'color',
      render: (value: number) => vehicleColors?.find((color) => color.id === value)?.name,
      sorter: (a, b) => a.color - b.color,
      filters: vehicleColors?.map((color) => ({ text: color.name, value: color.id })),
      filterSearch: true,
      onFilter: (value, record) => {
        return record.color === value;
      },
      responsive: ['xl'],
    },
    {
      title: 'Mileage',
      dataIndex: 'mileage',
      key: 'mileage',
      sorter: (a, b) => a.mileage - b.mileage,
      render: (mileage: number) => <>{mileage ? mileage.toLocaleString() : '-'} km</>,
      ...tableColumnTextFilterConfig<Vehicle>(),
      onFilter: (value, record) => {
        return record.mileage?.toString().toLowerCase().includes(value?.toString().toLowerCase());
      },
      responsive: ['xl'],
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status: VehicleStatus) => (
        <Tag color={VehicleStatusColorCode[status]}>{VehicleStatus[status]}</Tag>
      ),
      sorter: (a, b) => a.status - b.status,
      filters: statusFilter,
      onFilter: (value, record) => {
        return VehicleStatus[record.status] === value;
      },
    },
    {
      title: 'Last Updated On',
      dataIndex: 'updatedDate',
      key: 'updatedDate',
      render: (date: Date) => {
        return <>{formatDateTime(date)}</>;
      },
      sorter: (a, b) => new Date(a.updatedDate).getTime() - new Date(b.updatedDate).getTime(),
      defaultSortOrder: 'descend',
    },
  ];

  return (
    <>
      <BackTop />
      <PageHeader className='site-page-header' title='Next Auction Vehicles' />
      <Table
        dataSource={vehicles?.filter((vehicle) => vehicle.status === VehicleStatus.AuctionReady)}
        columns={columns}
        rowKey='id'
        loading={isLoading}
        size='small'
        onRow={(record, rowIndex) => {
          return {
            onClick: (event) => {
              navigate(`/vehicles/${record.id}`);
            },
          };
        }}
      />
    </>
  );
};

export default NextAuction;
